import type {
    MapDataLocationFeature,
    MapDataLocationFeatureProperties,
    MapDataLocationFeatureSessionProperties,
} from '@repo/types/maps/map-types';
import type { BulkUpdateGameMapPin } from '@repo/wemod-api-client/maps-api-client';

export interface BulkEditChanges {
    /** Empty string leaves each pin on its current subcategory. */
    subcategoryExternalId: string;
    /** null leaves every description untouched; a string (including '') overwrites every selected pin. */
    description: string | null;
}

export function hasBulkEditChange({ subcategoryExternalId, description }: BulkEditChanges): boolean {
    return subcategoryExternalId !== '' || description !== null;
}

/** Every selected pin is affected once any change is requested; nothing otherwise. */
export function selectAffectedFeatures(
    features: readonly MapDataLocationFeature[],
    changes: BulkEditChanges,
): MapDataLocationFeature[] {
    return hasBulkEditChange(changes) ? [...features] : [];
}

/**
 * bulk-update rewrites the whole pin server-side, so the payload must carry every stored
 * property (gameCoords, imagePath, ...) with the pin's current values. Session-only flags
 * are added client-side on map load and must not be written back.
 */
export function toPersistentProperties(
    properties: MapDataLocationFeatureProperties,
): Omit<MapDataLocationFeatureProperties, keyof MapDataLocationFeatureSessionProperties> {
    const persistent = { ...properties };
    delete persistent.selected;
    delete persistent.found;
    delete persistent.isCustomPin;
    delete persistent.isSharedCustomPin;
    delete persistent.hidden;
    delete persistent.supportsTeleport;
    return persistent;
}

export function buildBulkEditPayload(
    features: readonly MapDataLocationFeature[],
    { subcategoryExternalId, description }: BulkEditChanges,
): BulkUpdateGameMapPin[] {
    return features.map(feature => ({
        externalId: feature.properties.externalId,
        properties: {
            ...toPersistentProperties(feature.properties),
            ...(description !== null ? { description } : {}),
            ...(subcategoryExternalId !== '' ? { subcategoryExternalId } : {}),
        },
        geometry: feature.geometry,
    }));
}
